import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { notifyError, notifySuccess } from "../utils/notify";
import { useApp } from "../context/AppContext";
import { useNotifications } from "../context/NotificationContext";
import { getApiUrl, API_CONFIG } from "../config/api";
import { latestSyncSeq, takeNewSyncEvents } from "../utils/syncEventCursor";
import { AppointmentForm } from "../types/appointmentForms";
import {
  ClientFormSourceType,
  ClientFormStatus,
  ClientFormSummary,
  FormCategory,
  FormTemplate,
} from "../types/formTemplates";

/**
 * Adapter: the appointment-forms API (and the forms embedded in
 * GetTransactions) return the `AppointmentForm` shape (uuid + created_at/
 * updated_at); the shared list section consumes `ClientFormSummary`
 * (id + created_dt/updated_dt). Lossless for the fields the row UI touches.
 */
export function appointmentFormToSummary(
  form: AppointmentForm,
): ClientFormSummary {
  return {
    id: form.uuid,
    client_id: "",
    source_type: form.source_type as ClientFormSourceType,
    source_id: String(form.source_id),
    source_version: form.source_version ?? 1,
    name: form.name,
    category: form.category as FormCategory,
    state: form.state,
    is_opened: form.is_opened ?? false,
    is_edited: form.is_edited ?? false,
    is_submitted: form.is_submitted,
    submitted_at: form.submitted_at,
    client_submitted_at: form.client_submitted_at,
    staff_submitted_at: form.staff_submitted_at,
    expires_at: form.expires_at,
    validity_duration: null,
    status: form.status as ClientFormStatus,
    created_dt: form.created_at,
    updated_dt: form.updated_at,
    created_by: null,
    updated_by: null,
  };
}

interface UseAppointmentFormsOptions {
  /** Seed forms from GetTransactions (zero-fetch). When provided, the hook does
   *  not fetch on mount — used by the calendar popover. Omit in the modal to
   *  fetch fresh from the appointment-forms endpoint. */
  initialForms?: AppointmentForm[] | null;
  /** Required for the expire action (CLIENT_FORM_EXPIRE is salon/client scoped). */
  salonUuid?: string;
  clientUuid?: string;
  /** Called whenever the forms list changes after the initial seed (assign /
   *  remove / expire / refetch). Lets a parent keep the calendar's cached
   *  transaction (and the event popover) in sync with modal edits. */
  onFormsChange?: (forms: AppointmentForm[]) => void;
  /** For READ-ONLY consumers (e.g. the calendar popover) that don't mutate the
   *  list here: re-seed local state whenever the parent pushes a *new*
   *  `initialForms` reference (e.g. a status patch after the FormViewer closes),
   *  not just on the first mount. Leave off in mutating consumers (the modal's
   *  AppointmentFormsPanel) so their assign/remove/expire results aren't
   *  clobbered by a stale prop. */
  reseedOnInitialFormsChange?: boolean;
}

/**
 * Single-source forms logic shared by the Transaction Modal's
 * AppointmentFormsPanel and the calendar appointment drawer so the two can't
 * drift. Owns the forms list + the assign/remove/expire mutations; the
 * consuming component owns its own modal open/target state and calls these.
 */
export function useAppointmentForms(
  transactionUuid: string,
  {
    initialForms,
    salonUuid,
    clientUuid,
    onFormsChange,
    reseedOnInitialFormsChange,
  }: UseAppointmentFormsOptions = {},
) {
  const { fetchWithAuth } = useApp();
  const [forms, setForms] = useState<AppointmentForm[]>(initialForms ?? []);
  const [loading, setLoading] = useState<boolean>(initialForms == null);
  const [assigning, setAssigning] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [expiring, setExpiring] = useState(false);

  // onFormsChange is read via a ref so it never re-creates fetchForms / the
  // mutation callbacks. It fires from fetchForms (i.e. on every network refresh:
  // assign / remove / expire / FormViewer refetch) but NOT on the initial seed,
  // since seeding uses setForms(seed) directly.
  const onFormsChangeRef = useRef(onFormsChange);
  onFormsChangeRef.current = onFormsChange;

  const fetchForms = useCallback(async (): Promise<AppointmentForm[]> => {
    setLoading(true);
    try {
      const url = getApiUrl(API_CONFIG.ENDPOINTS.APPOINTMENT_FORMS, "", {
        transactionUuid,
      });
      const data = await fetchWithAuth(url);
      const list = (data as AppointmentForm[]) ?? [];
      setForms(list);
      onFormsChangeRef.current?.(list);
      return list;
    } catch (err) {
      console.error("Failed to fetch appointment forms:", err);
      notifyError("Could not load appointment forms.", { title: "Error" });
      return [];
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth, transactionUuid]);

  // Seed from initialForms when present (no network); otherwise fetch. Keyed on
  // transactionUuid so switching appointments (e.g. clicking a new calendar
  // event while the popover is mounted) reseeds/refetches exactly once.
  //
  // initialForms is read through a ref so the effect's dependency list stays
  // honest (only transactionUuid + fetchForms) without suppressing
  // react-hooks/exhaustive-deps — we re-seed only when the transaction changes,
  // not on every parent re-render (the array identity is unstable).
  const initialFormsRef = useRef(initialForms);
  initialFormsRef.current = initialForms;
  const lastTxnRef = useRef<string | null>(null);
  useEffect(() => {
    if (lastTxnRef.current === transactionUuid) return;
    lastTxnRef.current = transactionUuid;
    const seed = initialFormsRef.current;
    if (seed != null) {
      setForms(seed);
      setLoading(false);
    } else {
      void fetchForms();
    }
  }, [transactionUuid, fetchForms]);

  // Read-only consumers (the calendar popover) opt in here so a fresh
  // initialForms *reference* pushed by the parent — e.g. handleFormViewerClose
  // patching previewData.transaction.forms with refreshed statuses — is
  // mirrored into local state. The seed effect above only fires on
  // transactionUuid change, so without this the patch would be dropped. Guarded
  // by lastSeedRef so it never fires on mount or on an unchanged reference (no
  // loops), and a network refetch — which doesn't touch initialForms — is never
  // overwritten by a stale prop.
  const lastSeedRef = useRef(initialForms);
  useEffect(() => {
    if (!reseedOnInitialFormsChange || initialForms == null) return;
    if (lastSeedRef.current === initialForms) return;
    lastSeedRef.current = initialForms;
    setForms(initialForms);
  }, [reseedOnInitialFormsChange, initialForms]);

  // Live refresh: when CBP submits a portal form (or any other path mutates a
  // transaction), the backend fires a `transaction.sync` Pusher event.
  // NotificationContext collects those into pusherSyncEvents; we watch for new
  // ones and refetch when transaction_id matches ours.
  //
  // Cursor (last processed seq) is lazily initialized to the *latest* seq on
  // first run (not 0 like CalendarView). The hook mounts fresh every time a
  // modal or popover opens, and at that point the backlog is already reflected
  // by the seed (initialForms) or initial fetch — so we only care about events
  // arriving after mount. Starting at 0 would (a) race with the mount fetch in
  // the modal path and (b) silently overwrite the seed in the popover
  // zero-fetch path. Seq-based so it keeps working past the 50-event buffer cap.
  const { pusherSyncEvents } = useNotifications();
  const lastSeenSyncSeqRef = useRef<number | null>(null);
  useEffect(() => {
    if (lastSeenSyncSeqRef.current === null) {
      lastSeenSyncSeqRef.current = latestSyncSeq(pusherSyncEvents);
      return;
    }
    const { newEvents, lastSeenSeq } = takeNewSyncEvents(
      pusherSyncEvents,
      lastSeenSyncSeqRef.current,
    );
    if (newEvents.length === 0) return;
    lastSeenSyncSeqRef.current = lastSeenSeq;
    const matchesThisTransaction = newEvents.some(
      (ev) => ev?.transaction_id === transactionUuid,
    );
    if (matchesThisTransaction) {
      void fetchForms();
    }
  }, [pusherSyncEvents, transactionUuid, fetchForms]);

  // Mirror ClientFormsWaiversTab.handleUseTemplate: source_type depends on
  // whether the template is salon-custom or standard-library; source_id is the
  // template slug (library) or UUID id (custom). NEVER coerce to Number —
  // salon_form_templates.id is a UUID and Number(uuid) is NaN.
  const assign = useCallback(
    async (template: FormTemplate): Promise<AppointmentForm[] | null> => {
      setAssigning(true);
      try {
        const url = getApiUrl(API_CONFIG.ENDPOINTS.APPOINTMENT_FORMS, "", {
          transactionUuid,
        });
        await fetchWithAuth(url, {
          method: "POST",
          body: JSON.stringify({
            source_type:
              template.source === "custom" ? "salon_template" : "template",
            source_id: template.slug || template.id,
          }),
        });
        notifySuccess(`${template.name} assigned to appointment.`, {
          title: "Form Assigned",
        });
        // Return the refreshed list so callers can patch their own state
        // (e.g. the calendar popover) without re-fetching the transaction.
        return await fetchForms();
      } catch (err: any) {
        console.error("Failed to assign form:", err);
        notifyError(
          err?.errorBody?.message ?? "Could not assign form to appointment.",
          { title: "Error" },
        );
        return null;
      } finally {
        setAssigning(false);
      }
    },
    [fetchWithAuth, transactionUuid, fetchForms],
  );

  const remove = useCallback(
    async (form: AppointmentForm): Promise<boolean> => {
      setDeleting(true);
      try {
        const url = getApiUrl(
          API_CONFIG.ENDPOINTS.APPOINTMENT_FORM_REMOVE,
          "",
          {
            transactionUuid,
            clientFormUuid: form.uuid,
          },
        );
        await fetchWithAuth(url, { method: "DELETE" });
        notifySuccess(`${form.name} removed from appointment.`, {
          title: "Form Removed",
        });
        await fetchForms();
        return true;
      } catch (err: any) {
        console.error("Failed to remove form:", err);
        notifyError(err?.errorBody?.message ?? "Could not remove form.", {
          title: "Error",
        });
        return false;
      } finally {
        setDeleting(false);
      }
    },
    [fetchWithAuth, transactionUuid, fetchForms],
  );

  const expire = useCallback(
    async (form: AppointmentForm): Promise<boolean> => {
      if (!clientUuid || !salonUuid) return false;
      setExpiring(true);
      try {
        const url = getApiUrl(API_CONFIG.ENDPOINTS.CLIENT_FORM_EXPIRE, "", {
          salonId: salonUuid,
          clientId: clientUuid,
          formId: form.uuid,
        });
        await fetchWithAuth(url, { method: "POST", body: JSON.stringify({}) });
        notifySuccess(`${form.name} is now marked expired.`, {
          title: "Form expired",
        });
        await fetchForms();
        return true;
      } catch (err: any) {
        console.error("Failed to expire form:", err);
        notifyError(err?.errorBody?.message ?? "Try again.", {
          title: "Could not expire form",
        });
        return false;
      } finally {
        setExpiring(false);
      }
    },
    [fetchWithAuth, transactionUuid, salonUuid, clientUuid, fetchForms],
  );

  // Adapt to ClientFormSummary[] for the shared list section; memoized so the
  // section's filter/sort state isn't reset on every parent re-render.
  const summaryForms: ClientFormSummary[] = useMemo(
    () => forms.map(appointmentFormToSummary),
    [forms],
  );

  /** Back-reference to the original AppointmentForm (delete/viewer need it). */
  const formByUuid = useMemo(() => {
    const map = new Map<string, AppointmentForm>();
    for (const f of forms) map.set(f.uuid, f);
    return map;
  }, [forms]);

  const totalCount = forms.length;
  // "Pending" = client/staff still owe work; completed/expired forms don't make
  // a "fill out your forms" reminder meaningful.
  const pendingCount = useMemo(
    () =>
      forms.filter((f) => f.status !== "completed" && f.status !== "expired")
        .length,
    [forms],
  );

  return {
    forms,
    summaryForms,
    formByUuid,
    loading,
    assigning,
    deleting,
    expiring,
    assign,
    remove,
    expire,
    refetch: fetchForms,
    totalCount,
    pendingCount,
  };
}
